import { alpineData } from "./alpine-data";
import { getCSRFToken } from "./htmx-helpers";

export default (initialGroups: any[], initialAssets: any[], initialDeliverableId: string = "") => alpineData({
  activeGroup: initialGroups.length > 0 ? initialGroups[0].id : "",
  lightboxOpen: false,
  currentAsset: null,
  allAssets: initialAssets,
  groups: initialGroups,
  // Authoritative deliverable ID is passed in by the server-rendered template.
  // Never derive it from group data: a DeliverableGroup is linked to deliverables
  // many-to-many and carries no deliverable_id, so the old fallback produced
  // `undefined` and POSTed to /api/deliverables/undefined/feedback (400).
  deliverableId:
    initialDeliverableId || (initialGroups.length > 0 ? initialGroups[0].deliverable_id : ""),
  feedbackMode: false,
  selectedAssets: [],
  panelExpanded: true,

  init() {
    this.$watch("lightboxOpen", (val: boolean) => {
      document.body.style.overflow = val ? "hidden" : "";
    });

    // Live badge recolor. Two distinct sources need it:
    //   - manager resolve/reopen (change_request.go's NoContent sends
    //     HX-Trigger: change-requests-updated on an HTMX response, which HTMX
    //     dispatches on `body` and it bubbles to `window`);
    //   - a client filing a new request via submitLightboxFeedback() below,
    //     which is a raw fetch() — HX-Trigger response headers are inert for
    //     non-HTMX requests, so that method explicitly dispatches this same
    //     CustomEvent on success (see "feedback-submitted" listener wiring
    //     there) instead of relying on a header nothing would process.
    // Both funnel into one handler: refetch the small summary endpoint and
    // patch each asset in place so the grid badge (server-rendered
    // ChangeRequestIndicator, driven by cr_total/cr_open baked into the
    // initial x-data) and the lightbox "View history" button recolor/reappear
    // without a full poll-gallery re-render.
    // $store.crDrawer itself is registered separately, in deliverable_show.templ
    // (crDrawerStoreInit) — same idiom as the manage page — so there's nothing
    // to register here.
    const refreshChangeRequestBadges = async () => {
      try {
        const res = await fetch(`/api/deliverables/${this.deliverableId}/change-request-summary`, {
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!res.ok) return;
        const summary = await res.json(); // { [assetId]: { total, open } }
        this.allAssets.forEach((a: any) => {
          const s = summary[a.id];
          a.cr_total = s?.total ?? 0;
          a.cr_open = s?.open ?? 0;
        });
      } catch {
        // Best-effort refetch: the show gallery has no polling interval, so on
        // failure the badge simply keeps its last-known value until the next
        // change-requests-updated/feedback-submitted event fires, or the user
        // navigates and the page re-renders with fresh server data.
      }
    };
    window.addEventListener("change-requests-updated", refreshChangeRequestBadges);
    window.addEventListener("feedback-submitted", refreshChangeRequestBadges);
  },

  // Reactive per-asset change-request counts for the show-grid tile badge
  // (deliverable_gallery.templ). Reads from allAssets, which
  // refreshChangeRequestBadges() above patches in place — Alpine tracks
  // allAssets, so any x-show/:class/x-text bound via crCounts() re-evaluates
  // automatically when it's patched, without a gallery re-render.
  crCounts(assetId: string): { total: number; open: number } {
    const asset = this.allAssets.find((a: any) => a.id === assetId);
    return { total: asset?.cr_total ?? 0, open: asset?.cr_open ?? 0 };
  },

  openLightbox(asset: any) {
    this.currentAsset = asset;
    this.lightboxOpen = true;
  },

  openLightboxByID(id: string) {
    const asset = this.allAssets.find((a: any) => a.id === id);
    if (asset) {
      this.openLightbox(asset);
    }
  },

  next() {
    const media = this.allAssets.filter(
      (a: any) => a.content_type.startsWith("image/") || a.content_type.startsWith("video/"),
    );
    if (media.length === 0) return;
    const idx = media.findIndex((a: any) => a.id === this.currentAsset.id);
    if (idx === -1) {
      this.currentAsset = media[0];
      return;
    }
    this.currentAsset = media[(idx + 1) % media.length];
  },

  prev() {
    const media = this.allAssets.filter(
      (a: any) => a.content_type.startsWith("image/") || a.content_type.startsWith("video/"),
    );
    if (media.length === 0) return;
    const idx = media.findIndex((a: any) => a.id === this.currentAsset.id);
    if (idx === -1) {
      this.currentAsset = media[media.length - 1];
      return;
    }
    this.currentAsset = media[(idx - 1 + media.length) % media.length];
  },

  isAssetSelected(id: string) {
    return this.selectedAssets.includes(id);
  },

  toggleAssetSelection(id: string) {
    if (this.isAssetSelected(id)) {
      this.selectedAssets = this.selectedAssets.filter((item: string) => item !== id);
    } else {
      this.selectedAssets.push(id);
    }
  },

  toggleFeedbackMode() {
    this.feedbackMode = !this.feedbackMode;
    if (!this.feedbackMode) {
      this.selectedAssets = [];
    }
  },

  async submitLightboxFeedback(comment: string, assetIds: string[]) {
    if (!this.deliverableId) {
      console.error("Cannot submit feedback: deliverableId is missing on the gallery component");
      return false;
    }
    if (!assetIds || assetIds.length === 0) return false;
    try {
      const response = await fetch(`/api/deliverables/${this.deliverableId}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": (
            document.querySelector('input[name="gorilla.csrf.Token"]') as HTMLInputElement
          )?.value,
        },
        body: JSON.stringify({
          deliverable_id: this.deliverableId,
          deliverable_asset_ids: assetIds,
          comment: comment,
        }),
      });

      if (response.ok) {
        window.dispatchEvent(new CustomEvent("feedback-submitted"));
        return true;
      }
    } catch (err) {
      console.error("Failed to submit lightbox feedback:", err);
    }
    return false;
  },
});
